import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-regular-svg-icons';
import {
  SlideOutPanelShell,
  slideOutPanelShellStyles as styles,
  useExitRetained,
  useSlideOutPanelChrome,
} from '@cowbell/ui';
import type { Source, ThreatActorMetadata } from '../../types';
import { useThreatActorProfile } from '../../hooks/useThreatActorProfile';
import { renderPanelBody } from './panel-body-states';
import ThreatActorDetailBody from './ThreatActorDetailBody';

interface ThreatActorPanelProps {
  isOpen: boolean;
  onClose: () => void;
  source: Source | null;
  /** Width of the Ask Moo chat container (px) */
  chatWidth: number;
  /** Right offset of the chat container (px) — needed for floating mode */
  chatRight?: number;
}

/**
 * Fixed-position threat-actor intelligence panel docked to the LEFT of the
 * Ask Moo chat sidebar. Shares its chrome with CvePanel via
 * useSlideOutPanelChrome + SlideOutPanelShell (same CSS module too) and its
 * body state-gating via renderPanelBody, and seeds its header instantly
 * from the citation metadata while the rich profile lazy-loads by mitreId.
 *
 * Accessibility (provided by SlideOutPanelShell):
 * - role="dialog" + aria-modal="true" + aria-labelledby pointing at the title
 * - Focus moves into the panel on open; restored to trigger on close
 * - Focus is trapped within the panel while open (FocusTrap from @cowbell/ui)
 * - Esc key closes the panel
 * - prefers-reduced-motion disables slide/fade transitions (CSS media query)
 * - External links carry rel="noopener noreferrer"
 * - Resize handle accessible via mouse (keyboard resize not needed for panel chrome)
 */
export default function ThreatActorPanel({
  isOpen,
  onClose,
  source,
  chatWidth,
  chatRight = 0,
}: Readonly<ThreatActorPanelProps>) {
  // On close the caller clears `source` in the same turn; the panel keeps what it showed while it slides away.
  const { value: shown, exiting } = useExitRetained(source, isOpen && !!source);
  const meta = shown?.metadata as ThreatActorMetadata | undefined;

  const chrome = useSlideOutPanelChrome({
    isOpen,
    hasSource: !!source,
    onClose,
    chatWidth,
    chatRight,
  });

  const live = chrome.visible || exiting;
  const mitreId = live ? meta?.mitreId ?? null : null;

  const { profile, isLoading, isError, isNotFound, refetch } =
    useThreatActorProfile(mitreId, live ? meta : undefined);

  return (
    <SlideOutPanelShell
      chrome={chrome}
      onClose={onClose}
      styles={styles}
      headerTitle={meta?.name ?? 'Threat Actor Intelligence'}
      headerMeta={meta?.mitreId}
      headerLink={
        shown?.url && (
          <a
            className={styles.headerBtn}
            href={shown.url}
            target="_blank"
            rel="noopener noreferrer"
            title="Open MITRE ATT&CK page"
            aria-label="Open MITRE ATT&CK page"
          >
            <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
          </a>
        )
      }
    >
      {renderPanelBody({
        isNotFound,
        isError,
        profile,
        isLoading,
        notFoundMessage: `Threat actor ${meta?.mitreId} not found in the knowledge base.`,
        errorMessage: 'Failed to load threat actor profile.',
        loadingMessage: 'Loading threat actor…',
        refetch,
        styles,
        renderDetail: (p) => <ThreatActorDetailBody profile={p} />,
      })}
    </SlideOutPanelShell>
  );
}
